/**
 * MobileSignPage — öffentliches, passwortloses Unterschriften-Portal
 * (Jul-2026, User-Wunsch). Der Kunde/Verkäufer öffnet den vom PC gesendeten
 * Link/QR am Handy oder Tablet, verifiziert sich (Nachname + Geburtsdatum) und
 * unterschreibt Kauf- bzw. Ankaufvertrag auf dem Touchscreen.
 */
import { useRef, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { PenLine, Eraser, Loader2, CheckCircle2, FileText, ShieldCheck } from "lucide-react";
import DealerBrandLogo from "../components/brand/DealerBrandLogo";
import PublicLegalConsent from "../components/public/PublicLegalConsent";

const BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function SignatureCanvas({ label, testid, onChange }) {
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const dirty = useRef(false);
    const last = useRef({ x: 0, y: 0 });
    const pos = (e) => {
        const c = canvasRef.current; const rect = c.getBoundingClientRect();
        return { x: (e.clientX - rect.left) * (c.width / rect.width), y: (e.clientY - rect.top) * (c.height / rect.height) };
    };
    const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ } };
    const move = (e) => {
        if (!drawing.current) return; e.preventDefault();
        const ctx = canvasRef.current.getContext("2d"); const p = pos(e);
        ctx.strokeStyle = "#111827"; ctx.lineWidth = 2.4; ctx.lineCap = "round"; ctx.lineJoin = "round";
        ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
        last.current = p; dirty.current = true;
    };
    const end = () => { if (!drawing.current) return; drawing.current = false; if (dirty.current) onChange(canvasRef.current.toDataURL("image/png")); };
    const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); dirty.current = false; onChange(""); };
    useEffect(() => { const c = canvasRef.current; if (c) { c.width = 600; c.height = 180; } }, []);
    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-gray-600">{label}</span>
                <button type="button" onClick={clear} className="inline-flex items-center gap-1 text-[11px] font-bold text-gray-500 hover:text-red-600" data-testid={`${testid}-clear`}>
                    <Eraser className="w-3.5 h-3.5" /> Löschen
                </button>
            </div>
            <canvas ref={canvasRef}
                onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end} onPointerCancel={end}
                style={{ touchAction: "none" }}
                className="w-full h-[150px] bg-white border-2 border-dashed border-gray-300 rounded-sm cursor-crosshair"
                data-testid={testid} />
            <p className="text-[10px] text-gray-400">Mit Maus (Desktop) oder Finger/Stift (Touch) unterschreiben.</p>
        </div>
    );
}

export default function MobileSignPage() {
    const { token } = useParams();
    const [step, setStep] = useState("verify"); // verify | sign | done
    const [ln, setLn] = useState("");
    const [bd, setBd] = useState("");
    const [bdD, setBdD] = useState(""); const [bdM, setBdM] = useState(""); const [bdY, setBdY] = useState("");
    const setBdPart = (part, raw) => {
        const v = raw.replace(/\D/g, "");
        let d = bdD, m = bdM, y = bdY;
        if (part === "d") { d = v.slice(0, 2); setBdD(d); }
        if (part === "m") { m = v.slice(0, 2); setBdM(m); }
        if (part === "y") { y = v.slice(0, 4); setBdY(y); }
        setBd(d && m && y.length === 4 ? `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}` : "");
    };
    const [info, setInfo] = useState(null);
    const [branding, setBranding] = useState(null);
    const [buyerSig, setBuyerSig] = useState("");

    // Händler-Branding (Firmenname + Logo) für die Kopfzeile laden.
    useEffect(() => {
        fetch(`${BASE}/api/public/sign-portal/${token}/branding`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => d && setBranding(d))
            .catch(() => {});
    }, [token]);
    const [place, setPlace] = useState("");
    const [busy, setBusy] = useState(false);
    const [legalOk, setLegalOk] = useState(false);

    const post = async (path, body) => {
        const r = await fetch(`${BASE}/api/public/sign-portal/${token}/${path}`, {
            method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.detail || "Fehler");
        return data;
    };

    const verify = async (e) => {
        e.preventDefault();
        if (!ln.trim() || !bd) { toast.error("Bitte Nachname und Geburtsdatum eingeben"); return; }
        setBusy(true);
        try {
            const data = await post("verify", { last_name: ln, birth_date: bd });
            setInfo(data);
            setStep("sign");
        } catch (err) { toast.error(err.message); }
        setBusy(false);
    };

    const submit = async () => {
        if (!buyerSig) { toast.error("Bitte zuerst unterschreiben (Käufer)"); return; }
        setBusy(true);
        try {
            await post("submit", { last_name: ln, birth_date: bd, buyer_signature: buyerSig, place });
            setStep("done");
        } catch (err) { toast.error(err.message); }
        setBusy(false);
    };

    const pdfUrl = `${BASE}/api/public/sign-portal/${token}/document.pdf?ln=${encodeURIComponent(ln)}&bd=${encodeURIComponent(bd)}`;

    return (
        <div className="min-h-screen bg-[#F5F5F5] flex flex-col">
            <Toaster position="top-center" richColors />
            <header className="bg-white px-4 py-3 flex items-center justify-center border-b border-gray-200 shadow-sm">
                <DealerBrandLogo
                    variant="light"
                    logoUrl={info?.dealer_logo || branding?.dealer_logo}
                    companyName={info?.dealer_name || branding?.company_name}
                    imgClassName="h-9 max-w-[180px]"
                    textClassName="text-xl"
                />
            </header>

            <main className="flex-1 w-full max-w-lg mx-auto px-4 py-6">
                {step === "verify" && (
                    <form onSubmit={verify} className="bg-white border border-gray-200 rounded-sm p-5 space-y-4" data-testid="sign-verify-form">
                        <div className="flex items-center gap-2">
                            <ShieldCheck className="w-6 h-6 text-[#FF8300]" />
                            <h1 className="font-display font-black text-xl">Dokument unterschreiben</h1>
                        </div>
                        <p className="text-sm text-gray-600">Bitte bestätigen Sie Ihre Identität, um das Dokument anzusehen und zu unterschreiben.</p>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Nachname</label>
                            <input value={ln} onChange={(e) => setLn(e.target.value)} className="w-full mt-1 rounded-sm border border-gray-300 px-3 py-2 text-sm" data-testid="sign-verify-lastname" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Geburtsdatum</label>
                            <div className="flex items-center gap-2 mt-1">
                                <input inputMode="numeric" pattern="[0-9]*" maxLength={2} value={bdD} onChange={(e) => setBdPart("d", e.target.value)} placeholder="TT" className="w-14 rounded-sm border border-gray-300 px-2 py-2 text-sm text-center" data-testid="sign-verify-bd-day" />
                                <span className="text-gray-400">.</span>
                                <input inputMode="numeric" pattern="[0-9]*" maxLength={2} value={bdM} onChange={(e) => setBdPart("m", e.target.value)} placeholder="MM" className="w-14 rounded-sm border border-gray-300 px-2 py-2 text-sm text-center" data-testid="sign-verify-bd-month" />
                                <span className="text-gray-400">.</span>
                                <input inputMode="numeric" pattern="[0-9]*" maxLength={4} value={bdY} onChange={(e) => setBdPart("y", e.target.value)} placeholder="JJJJ" className="w-20 rounded-sm border border-gray-300 px-2 py-2 text-sm text-center" data-testid="sign-verify-birthdate" />
                            </div>
                            <p className="text-[10px] text-gray-400 mt-1">Als Ziffern eingeben, z. B. 05 . 03 . 1990</p>
                        </div>
                        <button type="submit" disabled={busy} className="w-full bg-[#111827] hover:bg-black text-white font-bold uppercase tracking-wider rounded-sm py-2.5 text-sm inline-flex items-center justify-center gap-2" data-testid="sign-verify-submit">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Weiter
                        </button>
                    </form>
                )}

                {step === "sign" && info && (
                    <div className="space-y-4" data-testid="sign-sign-step">
                        <div className="bg-white border border-gray-200 rounded-sm p-4">
                            <div className="text-xs uppercase tracking-wider text-gray-500 font-bold">{info.doc_label}</div>
                            <div className="font-display font-black text-lg">{info.vehicle_label}</div>
                            {info.already_signed && <div className="mt-1 text-xs text-emerald-700 font-semibold">Bereits unterschrieben — eine erneute Unterschrift überschreibt die vorherige.</div>}
                            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-[#FF8300] hover:underline" data-testid="sign-pdf-link">
                                <FileText className="w-4 h-4" /> Dokument als PDF öffnen
                            </a>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-sm p-4 space-y-4">
                            <SignatureCanvas label="Ihre Unterschrift" testid="sign-canvas-buyer" onChange={setBuyerSig} />
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Ort</label>
                                <input value={place} onChange={(e) => setPlace(e.target.value)} placeholder="z. B. München" className="w-full mt-1 rounded-sm border border-gray-300 px-3 py-2 text-sm" data-testid="sign-place" />
                            </div>
                            <PublicLegalConsent token={token} portal="sign" onAcceptedChange={setLegalOk} accent="#FF8300" />
                            <button onClick={submit} disabled={busy || !buyerSig || !legalOk} className="w-full bg-[#FF8300] hover:bg-[#e57600] disabled:opacity-50 text-white font-bold uppercase tracking-wider rounded-sm py-2.5 text-sm inline-flex items-center justify-center gap-2" data-testid="sign-submit-btn">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />} Rechtsverbindlich unterschreiben
                            </button>
                        </div>
                    </div>
                )}

                {step === "done" && (
                    <div className="bg-white border border-gray-200 rounded-sm p-6 text-center space-y-3" data-testid="sign-done">
                        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
                        <h1 className="font-display font-black text-xl">Vielen Dank!</h1>
                        <p className="text-sm text-gray-600">Ihre Unterschrift wurde erfolgreich übermittelt. Sie können dieses Fenster jetzt schließen.</p>
                    </div>
                )}
            </main>
        </div>
    );
}
